import React, { useState, useEffect } from 'react';
import { Check, Loader2, ShieldCheck, ArrowRight, Star, Users, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';
import { Reveal, SectionHead } from '../components/motion';
import { createCheckout } from '../lib/api';
import PayPalButton from '../components/PayPalButton';
import CryptoPayment from '../components/CryptoPayment';

const LOOKUP_KEYS = ['training_group', 'training_small', 'training_individual'];

export default function TrainingPayment() {
  const { t, lang } = useLang();
  const tiers = t('training.tiers');
  const [selected, setSelected] = useState(() => Math.max(0, tiers.findIndex((x) => !x.soldOut)));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await createCheckout({
        lookup_key: LOOKUP_KEYS[selected],
        origin_url: window.location.origin,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(t('form.error'));
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-28 pb-24" data-testid="training-payment-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead overline={t('pay.chooseLabel')} title={t('pay.title')} align="center" />
        <Reveal delay={0.1}>
          <p className="mt-5 text-center text-slate-400 max-w-2xl mx-auto" data-testid="payment-sub">{t('pay.sub')}</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-3 gap-2.5 sm:gap-5 items-stretch" data-testid="pay-tier-selector">
          {tiers.map((tierItem, i) => {
            const active = selected === i;
            const sold = !!tierItem.soldOut;
            return (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <button
                  type="button"
                  data-testid={`pay-tier-${i}`}
                  onClick={() => !sold && setSelected(i)}
                  disabled={sold}
                  aria-disabled={sold}
                  className={`relative w-full h-full rounded-xl border p-3 sm:p-6 text-center transition-all duration-300 ${
                    sold
                      ? 'border-slate-700/50 bg-[#1E293B]/40 opacity-60 cursor-not-allowed'
                      : active
                      ? 'border-blue-500 bg-[#1E293B] shadow-[0_0_45px_rgba(59,130,246,0.28)] sm:-translate-y-1.5 z-10'
                      : 'border-slate-700/70 bg-[#1E293B]/60 hover:border-slate-500'
                  }`}
                >
                  {sold ? (
                    <span data-testid={`pay-tier-soldout-${i}`} className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-red-500/90 px-2.5 py-1 text-[9px] sm:text-[10px] font-bold tracking-widest text-white whitespace-nowrap uppercase">
                      <Ban className="w-2.5 h-2.5" />
                      {t('common.soldOut')}
                    </span>
                  ) : tierItem.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-blue-500 px-2.5 py-1 text-[9px] sm:text-[10px] font-bold tracking-widest text-white whitespace-nowrap">
                      <Star className="w-2.5 h-2.5" />
                      {t('common.popular')}
                    </span>
                  )}
                  {!sold && (
                    <span className={`absolute top-2 right-2 sm:top-3 sm:right-3 flex w-4 h-4 sm:w-5 sm:h-5 items-center justify-center rounded-full border-2 transition-colors ${active ? 'border-blue-500 bg-blue-500' : 'border-slate-600'}`}>
                      {active && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
                    </span>
                  )}
                  <p className="text-[11px] sm:text-sm font-semibold text-slate-300 leading-tight pr-3 sm:pr-4">{tierItem.name}</p>
                  <p className={`mt-2 font-display text-xl sm:text-4xl whitespace-nowrap ${sold ? 'text-slate-500 line-through decoration-red-500/70' : 'text-white'}`}>{tierItem.price}</p>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-6 rounded-xl border border-slate-700/70 bg-[#1E293B] p-5 sm:p-6" data-testid="pay-tier-details">
            <p className="text-[11px] font-semibold tracking-widest text-slate-500 flex items-center justify-center gap-2">
              <Users className="w-3.5 h-3.5" /> {tiers[selected].size} · {tiers[selected].start}
            </p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl mx-auto">
              {tiers[selected].features.map((f, j) => (
                <li key={j} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-300">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-green-400" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-center text-xs text-slate-500">{tiers[selected].fit}</p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <button
              data-testid="pay-submit-btn"
              onClick={pay}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-500 px-10 py-5 text-lg font-semibold text-white transition-all duration-300 hover:bg-green-400 hover:shadow-[0_0_40px_rgba(34,197,94,0.5)] hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
              {t('pay.payBtn')} · {tiers[selected].price}
            </button>
            <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="pay-secure-note">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              {t('pay.note')}
            </p>
            <div className="mt-6">
              <PayPalButton lookupKey={LOOKUP_KEYS[selected]} />
            </div>
            <div className="mt-6 max-w-md mx-auto">
              <CryptoPayment lookupKey={LOOKUP_KEYS[selected]} amount={tiers[selected].price} />
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
